/**
 * Data Source Adapter Utilities - Thin Delegation Layer
 *
 * This module provides backwards-compatible functions that delegate to the new
 * interface-based adapter system. All complex logic has been moved to concrete
 * adapters (FulcrumAdapter, DjangoAdapter) while maintaining full backwards compatibility.
 *
 * ARCHITECTURE: Components → This File → AdapterFactory → Concrete Adapters
 */

import { ApiEndpoint, MediaItemDetailed } from "../types";

// Import the new adapter system
import { adapterFactory, createAdapterSystem } from "./adapters";
import { FetchOptions } from "./api";
import { isCmTableEndpoint } from "./cmTableEndpoint";
import { devLog } from "./devLog";

// ============================================================================
// Adapter System Initialization
// ============================================================================

// Initialize the adapter system on first import
let isInitialized = false;

const initializeAdapterSystem = () => {
  if (!isInitialized) {
    devLog("[AdapterSystem] NEW VERSION - Initialized successfully...");
    createAdapterSystem();
    isInitialized = true;
  }
};

// Initialize immediately
initializeAdapterSystem();

// ============================================================================
// Utility Functions (Preserved from original)
// ============================================================================

/**
 * Extract field value using dot notation path
 */
export const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Get the data source type for an item
 */
export const getDataSourceType = (item: any, endpoint?: any): string => {
  // PRIORITY 1: Use endpoint configuration (source of truth)
  if (endpoint?.dataSourceType) {
    return endpoint.dataSourceType;
  }

  // PRIORITY 2: Check for explicit data source markers
  if (item?._source?.type) {
    return item._source.type;
  }

  // PRIORITY 3: Infer from data structure (fallback)
  if (item?.fulcrum_data?.form_id || item?.fulcrum_photo?.photo_id) {
    return "fulcrum";
  }

  // PRIORITY 4: Detect CM Table endpoints
  if (isCmTableEndpoint(endpoint)) {
    return "cm-table";
  }

  // PRIORITY 5: Default to Django
  return "django";
};

/**
 * Helper to detect if a URL is an image
 */
export const isImageUrl = (url: string | null): boolean => {
  if (!url) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"];
  const lowerUrl = url.toLowerCase();
  return imageExtensions.some((ext) => lowerUrl.includes(ext)) || lowerUrl.includes("image");
};

/**
 * Extract field from data using field mapping
 */
export const extractField = (data: any, fieldPath: string | string[] | undefined, defaultValue: any = null): any => {
  if (!data || !fieldPath) return defaultValue;

  const paths = Array.isArray(fieldPath) ? fieldPath : [fieldPath];

  for (const path of paths) {
    const value = getNestedValue(data, path);
    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return defaultValue;
};

/**
 * Apply transformer function to field value
 */
export const applyTransformer = (
  fieldName: string,
  value: any,
  originalData: any,
  transformers?: Record<string, (value: any, originalData: any) => any>
): any => {
  if (transformers && transformers[fieldName]) {
    return transformers[fieldName](value, originalData);
  }
  return value;
};

/**
 * Standardize ID to number format
 */
export const standardizeId = (id: any): number => {
  if (typeof id === "number") return id;
  if (typeof id === "string") {
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// ============================================================================
// Safe Adapter Access (with error handling)
// ============================================================================

const getAdapterSafely = (dataSourceType: string) => {
  try {
    initializeAdapterSystem();
    return adapterFactory.getAdapter(dataSourceType);
  } catch (error) {
    console.warn(`[AdapterSystem] Failed to get adapter for ${dataSourceType}, falling back to django:`, error);
    return adapterFactory.getAdapter("django");
  }
};

// ============================================================================
// Backwards-Compatible Delegation Functions
// ============================================================================

/**
 * Fetch data from an endpoint - delegates to appropriate adapter
 */
export const fetchEndpointData = async (
  endpoint: ApiEndpoint,
  userProfile: any,
  options: {
    isLoadMore?: boolean;
    paginationUrl?: string;
    filters?: Record<string, string>;
    fields?: string;
  } = {},
  isPaginated?: boolean
): Promise<{
  data: any[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  } | null;
}> => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);
  return adapter.fetchEndpointData(endpoint, userProfile, options, isPaginated);
};

/**
 * Search an endpoint - delegates to appropriate adapter
 */
export const searchEndpoint = async (
  endpoint: ApiEndpoint,
  searchTerm: string,
  userProfile: any,
  options: {
    filters?: Record<string, string>;
    fields?: string;
  } = {}
): Promise<{
  data: any[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  } | null;
}> => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);
  return adapter.searchEndpoint(endpoint, searchTerm, userProfile, options);
};

/**
 * Get item name - delegates to appropriate adapter
 */
export const getDataItemName = (item: any, endpoint?: ApiEndpoint): string => {
  if (!item) return "Unknown Item";

  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    const result = adapter.getDataItemName(item, endpoint);
    return result || "Unknown Item";
  } catch (error) {
    console.warn(`[getDataItemName] Adapter error for ${dataSourceType}:`, error);
    return item.name || item.title || item.label || "Unknown Item";
  }
};

/**
 * Get item description - delegates to appropriate adapter
 */
export const getDataItemDescription = (item: any, endpoint?: ApiEndpoint): string => {
  if (!item) return "";

  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    const result = adapter.getDataItemDescription(item, endpoint);
    return result || "";
  } catch (error) {
    console.warn(`[getDataItemDescription] Adapter error for ${dataSourceType}:`, error);
    return item.description || item.notes || item.comment || "";
  }
};

/**
 * Get item thumbnail - delegates to appropriate adapter
 */
export const getItemThumbnail = (item: any, endpoint?: any): string | null => {
  if (!item) return null;

  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.getItemThumbnail(item, endpoint);
  } catch (error) {
    console.warn(`[getItemThumbnail] Adapter error for ${dataSourceType}:`, error);
    return item.thumbnail || null;
  }
};

/**
 * Get primary image - delegates to appropriate adapter
 */
export const getDataItemPrimaryImage = (item: any): string | null => {
  if (!item) return null;

  const dataSourceType = getDataSourceType(item);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.getDataItemPrimaryImage(item);
  } catch (error) {
    console.warn(`[getDataItemPrimaryImage] Adapter error for ${dataSourceType}:`, error);
    return item.primary_image?.url || item.thumbnail || null;
  }
};

/**
 * Get related media - delegates to appropriate adapter
 */
export const getRelatedMedia = (item: any): MediaItemDetailed[] => {
  if (!item) return [];

  const dataSourceType = getDataSourceType(item);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.getRelatedMedia(item);
  } catch (error) {
    console.warn(`[getRelatedMedia] Adapter error for ${dataSourceType}:`, error);
    return [];
  }
};

/**
 * Fetch item with relationships - delegates to appropriate adapter
 */
// LEVEL 1: Fetch minimal item data (name, description, thumbnail only)
export const fetchItemSummary = async (
  itemId: string | number,
  endpoint: ApiEndpoint,
  userProfile: any,
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);

  devLog(`🔵 [LEVEL 1] fetchItemSummary called:`, {
    itemId,
    dataSourceType,
    endpointName: endpoint.name,
    adapterType: adapter.constructor.name
  });

  try {
    const result = await adapter.fetchItemSummary(itemId, endpoint, userProfile, onProgress);
    devLog(`✅ [LEVEL 1] fetchItemSummary completed:`, {
      itemId,
      resultName: result?.name,
      hasCompleteData: result?.isComplete
    });
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 1] fetchItemSummary error for ${dataSourceType}:`, error);
    throw error;
  }
};

// LEVEL 2: Fetch complete item data (all properties but no relationships)
export const fetchItem = async (
  itemId: string | number,
  endpoint: ApiEndpoint,
  userProfile: any,
  allApiEndpoints: ApiEndpoint[] = [],
  onProgress?: (step: string, current: number, total: number) => void
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);

  devLog(`🟡 [LEVEL 2] fetchItem called:`, {
    itemId,
    dataSourceType,
    endpointName: endpoint.name,
    adapterType: adapter.constructor.name,
    includeRelationships: false
  });

  try {
    const result = await adapter.fetchItem(itemId, endpoint, userProfile, allApiEndpoints, onProgress);
    devLog(`✅ [LEVEL 2] fetchItem completed:`, {
      itemId,
      resultName: result?.name,
      hasCompleteData: result?.isComplete,
      hasRelationships: !!result?.relationships
    });
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 2] fetchItem error for ${dataSourceType}:`, error);

    // For Fulcrum "not found" errors, provide more context
    if (dataSourceType === "fulcrum" && error.message?.includes("not found")) {
      console.warn(`[fetchItem] Fulcrum record ${itemId} not found - may be deleted or moved`);
      throw new Error(`Record ${itemId} not found - it may have been deleted or moved`);
    }

    throw error;
  }
};

// LEVEL 3: Fetch complete item data with all relationships
export const fetchItemWithRelationships = async (
  itemId: string | number,
  endpoint: ApiEndpoint,
  userProfile: any,
  allApiEndpoints: ApiEndpoint[] = [],
  onProgress?: (step: string, current: number, total: number) => void,
  options?: FetchOptions
): Promise<any> => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);

  try {
    const result = await adapter.fetchItemWithRelationships(itemId, endpoint, userProfile, allApiEndpoints, onProgress, options);
    return result;
  } catch (error: any) {
    console.error(`❌ [LEVEL 3] fetchItemWithRelationships error for ${dataSourceType}:`, error);

    // For Fulcrum "not found" errors, provide more context
    if (dataSourceType === "fulcrum" && error.message?.includes("not found")) {
      console.warn(`[fetchItemWithRelationships] Fulcrum record ${itemId} not found - may be deleted or moved`);
      throw new Error(`Record ${itemId} not found - it may have been deleted or moved`);
    }

    throw error;
  }
};

/**
 * Extract feature data - delegates to appropriate adapter
 */
export const extractFeatureData = (properties: any, layerId: string, endpoint?: ApiEndpoint): any => {
  const dataSourceType = endpoint?.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.extractFeatureData(properties, layerId, endpoint);
  } catch (error) {
    console.warn(`[extractFeatureData] Adapter error for ${dataSourceType}:`, error);
    return {
      id: properties.id,
      name: properties.name,
      description: properties.description
    };
  }
};

// ============================================================================
// Fulcrum-Specific Utilities (Preserved for backwards compatibility)
// ============================================================================

/**
 * Detect Fulcrum region from form ID
 */
export const detectFulcrumRegion = (formId?: string): "au" | "us" => {
  if (!formId) return "au";

  // Australian forms typically have longer IDs (36+ chars with dashes)
  // US forms typically have shorter IDs (32 chars or less)
  if (formId.length > 30) {
    return "au";
  }

  return "us";
};

/**
 * Construct Fulcrum photo URL with region detection
 */
export const constructFulcrumPhotoUrl = (photoId: string, formId?: string, thumbnail: boolean = true): string => {
  if (!photoId) {
    throw new Error(`Invalid photoId: ${photoId}`);
  }

  const region = detectFulcrumRegion(formId);
  const baseUrl = region === "au" ? "https://api.fulcrumapp-au.com" : "https://api.fulcrumapp.com";
  const endpoint = thumbnail ? `/api/v2/photos/${photoId}/thumbnail.jpg` : `/api/v2/photos/${photoId}.jpg`;
  return `${baseUrl}${endpoint}`;
};

// ============================================================================
// Additional Missing Exports (for backwards compatibility)
// ============================================================================

/**
 * Find correct endpoint for an item by attempting to fetch it
 */
export const findCorrectEndpointForItem = async (
  itemId: string | number,
  itemName: string,
  apiEndpoints: ApiEndpoint[],
  currentEndpoint: ApiEndpoint
): Promise<ApiEndpoint> => {
  devLog("[EndpointFinder] Finding correct endpoint for item:", {
    itemId,
    itemName,
    currentEndpoint: currentEndpoint.name,
    availableEndpoints: apiEndpoints.map((ep) => ep.name)
  });

  // Skip validation for Fulcrum endpoints - they fetch data directly from Fulcrum API
  if (currentEndpoint.dataSourceType === "fulcrum") {
    devLog("[EndpointFinder] Fulcrum endpoint detected - skipping backend validation:", currentEndpoint.name);
    return currentEndpoint;
  }

  // For Django endpoints, try to fetch from the current endpoint first
  try {
    const { apiFetch, buildItemDetailUrl } = await import("./api");
    const url = buildItemDetailUrl(currentEndpoint.api_endpoint, itemId);
    await apiFetch(url);
    devLog("[EndpointFinder] Item found in current endpoint:", currentEndpoint.name);
    return currentEndpoint;
  } catch (error) {
    if (error instanceof Error && error.message.includes("404")) {
      devLog("[EndpointFinder] Item not found in current endpoint, trying others...");
    } else {
      console.warn("[EndpointFinder] Non-404 error, returning current endpoint:", error);
      return currentEndpoint;
    }
  }

  // Try other Django endpoints
  const { apiFetch, buildItemDetailUrl } = await import("./api");
  const djangoEndpoints = apiEndpoints.filter((ep) => ep.dataSourceType === "django" && ep.tableName !== currentEndpoint.tableName);

  for (const endpoint of djangoEndpoints) {
    try {
      const url = buildItemDetailUrl(endpoint.api_endpoint, itemId);
      await apiFetch(url);
      devLog("[EndpointFinder] ✅ Item found in endpoint:", endpoint.name);
      return endpoint;
    } catch (error) {
      if (error instanceof Error && error.message.includes("404")) {
        devLog("[EndpointFinder] Item not found in endpoint:", endpoint.name);
      } else {
        console.warn("[EndpointFinder] Error checking endpoint:", endpoint.name, error);
      }
    }
  }

  console.warn("[EndpointFinder] Item not found in any endpoint, returning current as fallback");
  return currentEndpoint;
};

/**
 * Check if endpoint is editable
 */
export const isEndpointEditable = (endpoint: { dataSourceType?: string; isEditable?: boolean }): boolean => {
  const dataSourceType = endpoint.dataSourceType || "django";
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.isEndpointEditable(endpoint);
  } catch (error) {
    console.warn(`[isEndpointEditable] Adapter error for ${dataSourceType}:`, error);
    return false; // Safe fallback - not editable if adapter fails
  }
};

/**
 * Open item for editing using data source-specific editing interface
 * - Fulcrum: Opens item in Fulcrum web interface (external redirect)
 * - Django: Returns false to use internal dialog (current behavior)
 */
export const openItemForEditing = (item: any, endpoint?: ApiEndpoint | null): boolean => {
  if (!item) return false;

  const dataSourceType = getDataSourceType(item, endpoint);
  const adapter = getAdapterSafely(dataSourceType);

  try {
    return adapter.openItemForEditing(item, endpoint || undefined);
  } catch (error) {
    console.warn(`[openItemForEditing] Adapter error for ${dataSourceType}:`, error);
    return false; // Fallback to internal dialog
  }
};

/**
 * Enhance map click data with data source-specific processing
 */
export const enhanceMapClickData = async (feature: any, layerId: string, endpoint?: ApiEndpoint, config?: any): Promise<any> => {
  const properties = feature.properties || {};

  // Extract basic data using the feature data extractor
  const extractedData = extractFeatureData(properties, layerId, endpoint);

  // Determine entity type and ID
  const entityType = endpoint?.tableName || layerId;
  // Trust the adapter's ID extraction - don't fall back to name which causes the 404 errors
  const entityId = String(extractedData.id || "unknown");
  const entityName = extractedData.name || `${layerId} Feature`;

  devLog("[enhanceMapClickData] Feature data extraction:", {
    extractedId: extractedData.id,
    extractedName: extractedData.name,
    extractionMethod: extractedData.extractionMethod,
    dataSourceType: endpoint?.dataSourceType,
    finalEntityId: entityId
  });

  // Handle geometry and coordinates
  let coordinates: [number, number] | undefined;
  if (feature.geometry?.type === "Point" && Array.isArray(feature.geometry.coordinates)) {
    coordinates = feature.geometry.coordinates as [number, number];
  }

  // Create the standardized geo entity
  const geoEntity = {
    id: entityId,
    name: entityName,
    entityType: entityType,
    layerId: layerId,
    coordinates: coordinates,
    geometry: feature.geometry,
    properties: {
      ...properties,
      ...extractedData,
      fromMapClick: true,
      hasCompleteData: false
    }
  };

  devLog("[MapClickEnhancer] Created geo entity:", {
    id: geoEntity.id,
    name: geoEntity.name,
    entityType: geoEntity.entityType,
    dataSourceType: endpoint?.dataSourceType || "generic"
  });

  return geoEntity;
};

/*
 * Map form fields (delegated to SharedAdapterUtils for unified logic)
 * Originally:
 * export const mapFormFields = (formValues: Record<string, any>, formSchema: any): Record<string, any> => {
 *   console.warn("[mapFormFields] This function should be refactored to use FulcrumAdapter");
 *   return formValues;
 * };
 */
export { mapFormFields, extractUnifiedDisplayName, extractUnifiedDisplayDescription } from "./adapters/SharedAdapterUtils";

// Unified extraction helpers are now re-exported from SharedAdapterUtils above
/*
 * Legacy implementations (kept for reference):
 *
 * export const extractUnifiedDisplayName = (options: FieldExtractionOptions): string => { ... }
 * export const extractUnifiedDisplayDescription = (options: FieldExtractionOptions): string => { ... }
 */

// ============================================================================
// Export Summary
// ============================================================================

devLog(`[AdapterSystem] Thin delegation layer loaded with backwards-compatible exports`);
